import { get, isObject } from 'lodash-es';
import * as React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import type {
  GroupBase,
  MultiValue,
  OnChangeValue,
  SingleValue,
} from 'react-select';

import type { IOptionItem } from '@wyz/types';
import clsx from 'clsx';
import {
  FormFeedback,
  FormGroup,
  Icon,
  Label,
  Select,
  SelectProps,
  Text,
  Tooltip,
  useConfiguredForm,
} from '../index';

export type SelectFieldProps<
  IsMulti extends boolean,
  Option extends IOptionItem<number | string> = IOptionItem<number | string>,
  Group extends GroupBase<Option> = GroupBase<Option>,
  TKeys extends string = string,
> = Pick<
  SelectProps<IsMulti, Option, Group>,
  | 'isClearable'
  | 'isSearchable'
  | 'isLoading'
  | 'required'
  | 'id'
  | 'options'
  | 'isMulti'
  | 'isDisabled'
  | 'placeholder'
  | 'size'
  | 'withCheckboxes'
  | 'wrapItems'
  | 'maxShown'
  | 'countMessage'
  | 'isOptionDisabled'
  | 'creatable'
  | 'components'
> & {
  name: string;
  helperText?: string;
  label?: React.ReactNode;
  defaultValue?: string | Array<string>;
  Trans: React.ComponentType<{
    i18nKey: TKeys;
    values?: Record<string, string>;
  }>;
  formGroupClassName?: string;
  feedbackType?: 'tooltip' | 'inline';

  /**
   * Only used to notify the parent about change, NOT to control the component
   */
  onSelect?: (args: OnChangeValue<Option, IsMulti>) => void;
  all?: {
    enabled?: boolean;
    selectAll?: string;
    unselectAll?: string;
  };
  tooltips?: React.ReactNode;
  renderReadMode?: (
    value: IsMulti extends true
      ? Array<Option> | undefined
      : Option | undefined,
  ) => React.ReactNode;
};

const flattenOptions = <T extends IOptionItem<string | number>>(
  options: Array<T | GroupBase<T>>,
) => {
  const flatten: Array<T> = [];
  options.forEach((option) => {
    if ('options' in option) {
      flatten.push(...option.options);
    } else {
      flatten.push(option);
    }
  });
  return flatten;
};
const ALL_VALUE = 'ALL';

function SelectField<
  IsMulti extends boolean,
  Option extends IOptionItem<string | number> = IOptionItem<string | number>,
  Group extends GroupBase<Option> = GroupBase<Option>,
  TKeys extends string = string,
>({
  Trans,
  onSelect,
  all,
  tooltips,
  formGroupClassName = 'mb-2',
  feedbackType = 'inline',
  renderReadMode,
  ...props
}: SelectFieldProps<IsMulti, Option, Group, TKeys>) {
  const options = props.options;
  const {
    getValues,
    formState: { errors, defaultValues },
    control,
  } = useFormContext();

  const getOptionValue = (option: Option) => option.value;

  const configurationContext = useConfiguredForm();
  const getValue = (
    selectedOption: SingleValue<Option> | MultiValue<Option> | null,
  ): Array<string | number> | string | number | null => {
    if (
      !Array.isArray(selectedOption) &&
      isObject(selectedOption) &&
      'value' in selectedOption
    ) {
      return getOptionValue(selectedOption);
    } else if (Array.isArray(selectedOption)) {
      return selectedOption?.map((item) => item.value);
    } else {
      return null;
    }
  };
  const names = props.name.split('.');
  const errorMessage = (get(errors, [...names, 'message']) ||
    get(errors, [names[0], 'message'])) as TKeys;
  const flatOptions = flattenOptions(options);
  const optionDefaultValue = props.isMulti
    ? (flatOptions.filter((option) =>
        get(defaultValues, props.name)?.includes(getOptionValue(option)),
      ) as MultiValue<Option>)
    : (flatOptions.find(
        (option) => getOptionValue(option) === get(defaultValues, props.name),
      ) as SingleValue<Option>);

  const DEFAULT_PLACEHOLDER_KEY =
    'app.common.datatable.filter.select.placeholder' as TKeys;

  return (
    <FormGroup className={formGroupClassName}>
      {props.label && (
        <div className='space-x-2 position-relative'>
          {tooltips && (
            <div className='position-absolute'>
              <Tooltip placement='right' label={tooltips}>
                <Icon icon='info-circle' width={13} height={13} />
              </Tooltip>
            </div>
          )}
          <Label
            htmlFor={props.id || props.name}
            className={clsx({
              'ms-3': tooltips,
              'required': props.required,
            })}
          >
            {props.label}
          </Label>
        </div>
      )}
      <Controller
        name={props.name}
        control={control}
        defaultValue={getValues(props.name)}
        render={({ field }) => {
          const currentValue = field.value;
          const value = Array.isArray(currentValue)
            ? flatOptions.filter((item) => currentValue.includes(item.value))
            : flatOptions.find((item) => item.value === currentValue);

          if (configurationContext.mode === 'READ_ONLY') {
            return props.isLoading ? (
              <div>
                <Icon icon='spinner' size='xs' />
              </div>
            ) : (
              <Text heading='normal' weight='semi-bold'>
                {renderReadMode
                  ? renderReadMode(
                      value as IsMulti extends true
                        ? Array<Option> | undefined
                        : Option | undefined,
                    )
                  : Array.isArray(value)
                    ? value.map((item) => item.label).join(',') || '-'
                    : value?.label || '-'}
              </Text>
            );
          }
          return (
            <>
              <Select<IsMulti, Option, Group>
                {...props}
                placeholder={
                  props.placeholder ?? (
                    <Trans i18nKey={DEFAULT_PLACEHOLDER_KEY} />
                  )
                }
                defaultValue={optionDefaultValue}
                value={value ?? null}
                options={options}
                showSelectAllButtons={all}
                onChange={(selectedOption) => {
                  field.onChange(getValue(selectedOption));
                  onSelect?.(selectedOption);
                }}
                isOptionSelected={(option, selectValue) => {
                  if (getOptionValue(option) === ALL_VALUE) {
                    return selectValue.length === flatOptions.length;
                  }
                  const selectedValues = selectValue.map((o) => o.value);
                  return selectedValues.includes(getOptionValue(option));
                }}
                isError={!!errorMessage}
                data-cy={props.name}
                ref={field.ref}
              />
            </>
          );
        }}
      />
      {errorMessage ? (
        <FormFeedback
          invalid
          tooltip={feedbackType === 'tooltip'}
          id={`${props.name}.error`}
        >
          <Trans i18nKey={errorMessage} />
        </FormFeedback>
      ) : props.helperText ? (
        <FormFeedback>{props.helperText}</FormFeedback>
      ) : null}
    </FormGroup>
  );
}

export default React.memo(SelectField) as typeof SelectField;
